import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../../../constants/common'
import {
  getGretaAIChatAskElsewhereOrigin,
  getGretaAIChatAskElsewhereTarget,
  getGretaAIChatLastQuestionText,
  getIsGretaAIChatAskElsewhereReady,
} from '../../../GretaAIChat.utils.askElsewhere'
import {
  getIsGretaAIChatFinanceAvailable,
  getIsGretaAIChatFinanceNew,
  getIsGretaAIChatFinanceNotBought,
  getIsGretaAIChatFinancePreview,
  getIsGretaAIChatFinancePreviewPick,
  getIsGretaAIChatSourceChoiceEnabled,
  getIsGretaAIChatSourceRevoked,
} from '../../../GretaAIChat.utils.financeAccess'
import {
  getCanGretaAIChatSwitchSource,
  getGretaAIChatOpenSource,
  getGretaAIChatSourceMessages,
  getIsGretaAIChatNewChat,
  getIsGretaAIChatSourceOutage,
  getIsGretaAIChatSourcePinned,
  getIsGretaAIChatSourceSwitchShown,
} from '../../../GretaAIChat.utils.source'
import {
  getGretaAIChatLauncherTooltip,
  getGretaAIChatSourcePlaceholderKey,
  getGretaAIChatUnreadSources,
  getIsGretaAIChatFinanceOnlyUnread,
} from '../../../GretaAIChat.utils.sourceTexts'

export function useGretaAIChatSessionSource({
  activeChatId,
  chats,
  chatSource,
  chatStatusById,
  financeAccessState,
  handleSendMessage,
  hasUnread,
  isLimitReached,
  isLocked,
  isSendPending,
  messages,
  openNewChat,
  status,
}) {
  const { t } = useTranslation()

  const askElsewhereRef = useRef(null)

  const { financeAccess, markPreviewSeen } = financeAccessState
  const {
    pinSource,
    selectSource,
    source: selectedSource,
    sourceSwitchCount,
  } = chatSource

  const isPinned = getIsGretaAIChatSourcePinned({ activeChatId, messages })

  const isSourceChoiceEnabled =
    getIsGretaAIChatSourceChoiceEnabled(financeAccess)

  const source = getGretaAIChatOpenSource({
    isPinned,
    isSourceChoiceEnabled,
    source: selectedSource,
  })

  const askElsewhereTarget = getGretaAIChatAskElsewhereTarget({
    isFinanceAvailable: getIsGretaAIChatFinanceAvailable(financeAccess),
    isSourceChoiceEnabled,
    messages,
    source,
    status,
  })

  const isFinancePreview = getIsGretaAIChatFinancePreview({
    financeAccess,
    isPinned,
    source,
  })

  const isSourceRevoked = getIsGretaAIChatSourceRevoked({
    financeAccess,
    isPinned,
    source,
  })

  const isSourceOff = isSourceRevoked || isFinancePreview

  const isSourceOutage = getIsGretaAIChatSourceOutage({ source, status })

  const unreadSources = getGretaAIChatUnreadSources(chatStatusById, chats)

  function handleSelectSource(nextSource) {
    if (
      getIsGretaAIChatFinancePreviewPick({ financeAccess, source: nextSource })
    ) {
      markPreviewSeen()
    }

    selectSource(nextSource)
  }

  // The new chat is created with the source shown when the question was asked.
  function handleSendMessageWithSource(text, creationOptions = EMPTY_OBJECT) {
    if (getIsGretaAIChatNewChat(activeChatId)) {
      pinSource(source)
    }

    handleSendMessage(text, { ...creationOptions, source })
  }

  // The question goes out once the new chat is open and its feed is empty.
  function handleAskElsewhere() {
    askElsewhereRef.current = {
      origin: getGretaAIChatAskElsewhereOrigin(activeChatId),
      question: getGretaAIChatLastQuestionText(messages),
    }
    selectSource(askElsewhereTarget)
    openNewChat()
  }

  useEffect(() => {
    const request = askElsewhereRef.current

    if (
      getIsGretaAIChatAskElsewhereReady({
        activeChatId,
        isSendPending,
        messages,
        request,
      })
    ) {
      askElsewhereRef.current = null
      handleSendMessageWithSource(request.question, { origin: request.origin })
    }
  }, [activeChatId, isSendPending, messages])

  return {
    launcher: {
      isFinanceUnread: getIsGretaAIChatFinanceOnlyUnread(unreadSources),
      tooltip: getGretaAIChatLauncherTooltip({
        hasUnread,
        isSourceChoiceEnabled,
        t,
        unreadSources,
      }),
    },
    messages: getGretaAIChatSourceMessages({
      isSourceOff,
      isSourceOutage,
      messages,
    }),
    source: {
      askElsewhereTarget,
      canSwitch: getCanGretaAIChatSwitchSource({
        isLimitReached,
        isPinned,
        isSendPending,
        isSourceChoiceEnabled,
      }),
      isFinanceNew: getIsGretaAIChatFinanceNew(financeAccess),
      isFinanceNotBought: getIsGretaAIChatFinanceNotBought(financeAccess),
      isFinancePreview,
      isPinned,
      isSourceChoiceEnabled,
      isSourceOff,
      isSourceOutage,
      isSourceRevoked,
      isSourceSwitchShown: getIsGretaAIChatSourceSwitchShown({
        isLocked,
        isSourceChoiceEnabled,
      }),
      placeholderKey: getGretaAIChatSourcePlaceholderKey({
        isFinancePreview,
        isSourceChoiceEnabled,
        isSourceRevoked,
        source,
      }),
      source,
      sourceSwitchCount,
      onAskElsewhere: handleAskElsewhere,
      onSelectSource: handleSelectSource,
    },
    onSendMessage: handleSendMessageWithSource,
  }
}
